/**
 * What a customer reads when the router refuses a creation because an allowance is spent
 * (app#296).
 *
 * ⚠⚠ THE REFUSAL USED TO BE A NATIVE `alert()`, AND IT BLOCKED THE PAGE. Measured on prod
 * 2026-09-07 06:08 UTC, a Pro organization with its allowance drawn to zero pressed Submit in New
 * Network and got
 *
 *     Network addition error: usage credit is empty — buy credit to add resources, or wait
 *     until the next billing period, when the included allowance comes back
 *
 * in browser chrome, behind a developer-facing prefix, with the JS thread halted until it was
 * dismissed. Two screen reads timed out with "page is busy" while the dialog stood.
 *
 * ⚠⚠ THE ROUTER'S SENTENCE IS THE GOOD PART AND IS USED VERBATIM. `src/tools/limits.go` writes
 * one message per limit type and each names every way out, one of which is free — "buy credit …
 * or wait until the next billing period", "upgrade one to a paid plan, or have someone else own
 * the next one". Rewording them here would put a second copy of that judgement in a second repo,
 * and the copy would drift. This file drops the `'Network addition error: '` prefix and carries
 * the rest through untouched.
 *
 * ⚠ THIS IS NOT `billingError.ts`. That file exists because a money failure reaches the browser
 * as a Go driver error nobody may read; this one exists because a limit refusal reaches it as
 * good copy nobody could act on. Opposite defects, so opposite treatments: one replaces the
 * router's text, the other preserves it.
 */

/**
 * The router's `CodeResourceExhausted`, on the wire.
 *
 * ⚠ CONFIRMED FROM THE ROUTER SOURCE, NOT GUESSED. `src/tools/errors.go:36` declares
 * `CodeResourceExhausted ErrorCode = "resource_exhausted"`, `src/tools/limits.go:63` is the only
 * constructor a creation cap reaches, and `src/api/v1/router.go:304` JSON-encodes the whole
 * `*ToolError` flat — so `code`, `message` and `details` arrive as written, with no envelope
 * around them.
 */
const RESOURCE_EXHAUSTED = 'resource_exhausted'

/** A creation refusal, ready to render. */
export type LimitRefusal = {
  /** The router's message, verbatim and unprefixed. */
  message: string
  /** `credit_empty`, `organizations`, `bandwidth`, … — `''` when the payload carried none. */
  limitType: string
  /** Present only when the router sent a destination this app may send a customer to. */
  upgradeUrl?: string
}

/**
 * Whether a destination the router named may be put behind a button.
 *
 * ⚠⚠ IT IS AN APP PATH IN PRODUCTION, NOT AN ABSOLUTE URL, and accepting only `http(s)` would
 * have made the Upgrade action dead code on the one deployment that matters. `config.BillingURL`
 * defaults to `/billing` (`src/config/config.go:458`) and no terraform or workflow sets
 * `NOBGP_BILLING_URL`, so what actually arrives in `details.upgrade_url` today is the string
 * `/billing` — which is a real route in this app (`router.tsx:67`).
 *
 * So both shapes are admitted and nothing else is: an absolute `http`/`https` URL, or a
 * same-origin path. ⚠ `//evil.example` is a PROTOCOL-RELATIVE URL, not a path — it starts with
 * `/` and leaves the origin — so the path arm rejects it explicitly, along with the backslash
 * spellings (`/\evil.example`) the URL parser resolves the same way. `javascript:` and `data:`
 * match neither arm.
 */
const usableUpgradeUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value) return undefined
  // ⚠⚠ `//host` IS NOT THE ONLY PROTOCOL-RELATIVE SPELLING. The URL parser treats a backslash as a
  // slash for special schemes, so `/\host` and `/\\host` leave the origin exactly as `//host` does —
  // measured: `new URL('/\evil.example', 'https://app.nobgp.com')` is `https://evil.example/`. A
  // second character of `/` or `\` therefore means an authority follows, never a path.
  if (/^\/[/\\]/.test(value)) return undefined
  if (value.startsWith('/')) return value
  try {
    const { protocol } = new URL(value)
    return protocol === 'http:' || protocol === 'https:' ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * The refusal behind a thrown value, or `null` when it is not one.
 *
 * ⚠ READ STRUCTURALLY, so any throw is handled and none is trusted — the same rule
 * `billingError.ts` follows, and for the same reason: this must not import `lib/tools.ts` (and
 * with it the Supabase client and `import.meta.env`) into a plain Node test script.
 *
 * ⚠ `details` MAY BE ABSENT and that is not a failure. The message alone is worth rendering; only
 * the Upgrade action depends on the payload.
 */
export const limitRefusal = (thrown: unknown): LimitRefusal | null => {
  if (!(thrown instanceof Error)) return null

  const bag = thrown as unknown as Record<string, unknown>
  if (bag.code !== RESOURCE_EXHAUSTED) return null

  const message = thrown.message.trim()
  if (!message) return null

  const details =
    typeof bag.details === 'object' && bag.details !== null && !Array.isArray(bag.details)
      ? (bag.details as Record<string, unknown>)
      : {}

  const upgradeUrl = usableUpgradeUrl(details.upgrade_url)
  return {
    message,
    limitType: typeof details.limit_type === 'string' ? details.limit_type : '',
    ...(upgradeUrl ? { upgradeUrl } : {}),
  }
}
